import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import AllowlistPage from './pages/AllowlistPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { DataProvider } from './contexts/DataContext';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import PlayerListPage from './pages/PlayerListPage';
import PlayerDetailPage from './pages/PlayerDetailPage';
import GameLibraryPage from './pages/GameLibraryPage';
import NewGameLibraryPage from './pages/NewGameLibraryPage';
import GameEditLibraryPage from './pages/GameEditLibraryPage';
import GameDetailPage from './pages/GameDetailPage';
import PlayListPage from './pages/PlayListPage';
import NewPlayPage from './pages/NewPlayPage';
import PlayDetailPage from './pages/PlayDetailPage';
import PlayEditPage from './pages/PlayEditPage';
import PlayRecapPage from './pages/PlayRecapPage';
import LivePlayPage from './pages/LivePlayPage';
import TurnTimerPage from './pages/TurnTimerPage';
import FirstPlayerMinigamePage from './pages/FirstPlayerMinigamePage';
import CoinTossPage from './pages/CoinTossPage';
import FingerPickerPage from './pages/FingerPickerPage';
import DiceRollerPage from './pages/DiceRollerPage';
import LeagueListPage from './pages/LeagueListPage';
import NewLeaguePage from './pages/NewLeaguePage';
import LeagueDetailPage from './pages/LeagueDetailPage';
import LeagueEditPage from './pages/LeagueEditPage';
import GameToolsPage from './pages/GameToolsPage';
import GamePickerPage from './pages/GamePickerPage';
import RulesChatToolPage from './pages/RulesChatToolPage';
import InvitesPage from './pages/InvitesPage';
import InstallBanner from './components/InstallBanner';
import LoginPage from './pages/LoginPage';
import OnboardingPage from './pages/OnboardingPage';
import NewPlayerPage from './pages/NewPlayerPage';
import UsagePage from './pages/UsagePage';
import PrivacyPage from './pages/PrivacyPage';
import TelemetryTracker from './components/TelemetryTracker';
import './aws-config';
import { OpenAPI } from './generated-api';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import { installApiTiming } from './services/telemetry';
import { installRatingHistoryInvalidation } from './services/ratingHistories';

// Configure API base URL
if (process.env.REACT_APP_DOCKER_BACKEND_URL) {
  OpenAPI.BASE = process.env.REACT_APP_DOCKER_BACKEND_URL;
} else if (process.env.NODE_ENV === 'development') {
  OpenAPI.BASE = 'http://localhost:8000';
}

// Configure API to send JWT token with every request
OpenAPI.TOKEN = async () => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token || '';
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return '';
  }
};

// Record API latency/failures for usage telemetry (no-op until telemetry starts)
installApiTiming(axios);

// Cached rating histories must not outlive a play or league change made from this browser
installRatingHistoryInvalidation(axios);

// Component to handle onboarding redirect
const OnboardingRedirect: React.FC = () => {
  const { onboardingRequired } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Define routes that are only accessible during onboarding
    const onboardingOnlyRoutes = ['/welcome'];
    const isOnOnboardingOnlyRoute = onboardingOnlyRoutes.some(route =>
      location.pathname === route || location.pathname.startsWith(route + '/')
    );
    
    // Routes accessible during onboarding (but also accessible after)
    const onboardingAccessibleRoutes = ['/welcome', '/players/new'];
    const isOnOnboardingAccessibleRoute = onboardingAccessibleRoutes.some(route =>
      location.pathname === route || location.pathname.startsWith(route + '/')
    );

    if (onboardingRequired) {
      // User needs onboarding - redirect to welcome from any non-onboarding route
      if (!isOnOnboardingAccessibleRoute) {
        console.log('🔄 Redirecting to onboarding page - profile not claimed');
        navigate('/welcome', { replace: true });
      }
    } else {
      // User doesn't need onboarding - redirect away from onboarding-only pages to home
      // But allow /players/new since users can create additional players
      if (isOnOnboardingOnlyRoute) {
        console.log('🔄 Redirecting to home - user already has profile');
        navigate('/', { replace: true });
      }
    }
  }, [onboardingRequired, location.pathname, navigate]);

  return null;
};

const AuthenticatedApp: React.FC = () => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const { sessionInitialized } = useSession();

  // Show loading screen while auth is checking OR session is being initialized
  if (isLoading || (isAuthenticated && !sessionInitialized)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <Layout>
      <OnboardingRedirect />
      <TelemetryTracker />
      <Routes>
        {/* Onboarding route - accessible when user needs to select/create profile */}
        <Route path="/welcome" element={<OnboardingPage />} />
        <Route path="/admin/allowlist" element={isAdmin ? <AllowlistPage /> : <Navigate to="/" replace />} />
        <Route path="/admin/invites" element={isAdmin ? <InvitesPage /> : <Navigate to="/" replace />} />
        <Route path="/admin/usage" element={isAdmin ? <UsagePage /> : <Navigate to="/" replace />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/players" element={<PlayerListPage />} />
        <Route path="/players/new" element={<NewPlayerPage />} />
        <Route path="/players/:id" element={<PlayerDetailPage />} />
        
        {/* League Routes */}
        <Route path="/leagues" element={<LeagueListPage />} />
        <Route path="/leagues/new" element={<NewLeaguePage />} />
        <Route path="/leagues/:id" element={<LeagueDetailPage />} />
        <Route path="/leagues/:id/edit" element={<LeagueEditPage />} />
        
        {/* Game Library Routes */}
        <Route path="/games" element={<GameLibraryPage />} />
        <Route path="/games/new" element={<NewGameLibraryPage />} />
        <Route path="/games/:id" element={<GameDetailPage />} />
        <Route path="/games/:id/rules" element={<GameDetailPage />} />
        <Route path="/games/:id/strategy" element={<GameDetailPage />} />
        <Route path="/games/:id/files" element={<GameDetailPage />} />
        <Route path="/games/:id/chat" element={<GameDetailPage />} />
        <Route path="/games/:id/edit" element={<GameEditLibraryPage />} />
        
        {/* Play Session Routes */}
        <Route path="/plays" element={<PlayListPage />} />
        <Route path="/plays/new" element={<NewPlayPage />} />
        <Route path="/plays/:id" element={<PlayDetailPage />} />
        <Route path="/plays/:id/recap" element={<PlayRecapPage />} />
        <Route path="/plays/:id/edit" element={<PlayEditPage />} />
        
        {/* Live Play Routes */}
        <Route path="/live-plays/:id" element={<LivePlayPage />} />
        <Route path="/live-plays/:id/turn-timer" element={<TurnTimerPage />} />
        <Route path="/live-plays/:id/first-player" element={<FirstPlayerMinigamePage />} />
        <Route path="/live-plays/:id/coin-toss" element={<CoinTossPage />} />
        <Route path="/live-plays/:id/finger-picker" element={<FingerPickerPage />} />

        {/* Game Tools (standalone) */}
        <Route path="/tools" element={<GameToolsPage />} />
        <Route path="/tools/coin-toss" element={<CoinTossPage />} />
        <Route path="/tools/finger-picker" element={<FingerPickerPage />} />
        <Route path="/tools/first-player" element={<FirstPlayerMinigamePage />} />
        <Route path="/tools/dice-roller" element={<DiceRollerPage />} />
        <Route path="/tools/game-picker" element={<GamePickerPage />} />
        <Route path="/tools/rules-chat" element={<RulesChatToolPage />} />
      </Routes>
      <InstallBanner />
    </Layout>
  );
};

function App() {
  return (
    <SessionProvider>
      <AuthProvider>
        <DataProvider>
          <Router>
            <AuthenticatedApp />
          </Router>
        </DataProvider>
      </AuthProvider>
    </SessionProvider>
  );
}

export default App;
